import {StackScreenProps} from '@react-navigation/stack';
import {Box, Button, Toast} from 'native-base';
import React, {useEffect, useRef, useState} from 'react';
import {Linking, TextInput} from 'react-native';
import {ScrollView} from 'react-native-gesture-handler';
import {SafeAreaView} from 'react-native-safe-area-context';
import {t} from 'react-native-tailwindcss';
import {
  useBalanceQuery,
  useCreateRechargeMutation,
  useOnBalanceUpdatedSubscription
} from '../api/generated';
import {AmountInput} from '../components/AmountInput';
import {KeyboardAvoidingView} from '../components/KeyboardAvoidingView';
import {formatIntAmount} from '../lib/formatters';
import {triggerHapticFeedback} from '../lib/hapticFeedback';
import i18n from '../lib/i18n';
import {config} from '../stage.config';
import {Nav, NavParams} from './navigation';

export const RechargeScreen: React.FC<StackScreenProps<NavParams, Nav.RechargeScreen>> = ({
  navigation
}) => {
  const {data: balanceData} = useBalanceQuery();
  const [createRecharge, {loading: isCreatingRecharge}] = useCreateRechargeMutation();
  const balance = balanceData?.balance || 0;
  const scrollRef = useRef<ScrollView>(null);
  const inputRef = useRef<TextInput>(null);
  const maxAmountToRecharge = config.maxBalance - balance;
  const [amount, setAmount] = useState(0);

  useOnBalanceUpdatedSubscription({
    onSubscriptionData: () => {
      Toast.show({
        description: i18n.t('rechargeSuccess'),
        duration: 3000
      });
      return navigation.navigate(Nav.HomeScreen);
    }
  });

  useEffect(() => {
    return navigation.addListener('transitionEnd', (nativeEvent) => {
      if (!nativeEvent.data.closing) {
        inputRef.current?.focus();
      }
    });
  }, [navigation]);

  const adjustAmount = () => {
    if (amount === maxAmountToRecharge) {
      setAmount(0);
      inputRef.current?.focus();
    } else {
      setAmount(maxAmountToRecharge);
      inputRef.current?.blur();
    }
  };

  const handleRecharge = async () => {
    if (amount > maxAmountToRecharge) return;
    const {data} = await createRecharge({variables: {amount}});
    const redirectUrl = data?.createRecharge.redirectUrl;
    const mustRedirect = data?.createRecharge.mustRedirect;
    if (!redirectUrl) return;
    triggerHapticFeedback();
    if (mustRedirect) {
      return Linking.openURL(redirectUrl);
    }
    navigation.replace(Nav.RechargeViewScreen, {url: redirectUrl});
  };

  const isValid = amount > 0 && amount <= maxAmountToRecharge;

  return (
    <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
      <KeyboardAvoidingView offset={125}>
        <ScrollView
          style={[t.flex1]}
          contentContainerStyle={[t.flexGrow]}
          keyboardDismissMode="interactive"
          ref={scrollRef}>
          <Box style={[t.flex1, t.justifyCenter]}>
            <AmountInput
              ref={inputRef}
              amount={amount}
              onError={() => scrollRef.current?.scrollToEnd()}
              onAmountChange={setAmount}
              onSubmit={handleRecharge}
              maxAmount={maxAmountToRecharge}
              hint={i18n.t('rechargeMessage', {amount: formatIntAmount(config.maxBalance)})}
              getMaxAmountErrorMessage={(maxAmountString) =>
                i18n.t('maxRechargeError', {amount: maxAmountString})
              }
            />
          </Box>
        </ScrollView>
        <Button style={[t.mX4]} variant="ghost" onPress={adjustAmount}>
          {amount === maxAmountToRecharge ? i18n.t('partialRecharge') : i18n.t('fullRecharge')}
        </Button>
        <Button
          style={[t.m4, t.mT4]}
          isDisabled={!isValid}
          onPress={handleRecharge}
          isLoading={isCreatingRecharge}>
          {`${i18n.t('recharge')} ${formatIntAmount(amount)}`}
        </Button>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};
